import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import CategoryMultiSelect from './CategoryMultiSelect';
import BrandSelect from './BrandSelect';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors, createProduct } from '../../actions/productAction';
import { NEW_PRODUCT_RESET } from '../../constants/productConstants';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Reads a File as a base64 data URL
const readAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const emptyVariation = () => ({ mode: "inline", name: "", price: "", cuttedPrice: "", categories: [], images: [], brand: { name: "", logo: null }, linkedProduct: null });

const NewProduct = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, error, success } = useSelector((state) => state.newProduct);

    // --- basic info ---
    const [name, setName] = useState("");
    const [nameZh, setNameZh] = useState("");
    const [mainImage, setMainImage] = useState("");
    const [mainImagePreview, setMainImagePreview] = useState("");
    const [additionalImages, setAdditionalImages] = useState([]); // array of base64 strings
    const [price, setPrice] = useState(""); // regular price
    const [salePrice, setSalePrice] = useState(""); // actual selling price
    const [categories, setCategories] = useState([]); // array of category ids
    const [brandName, setBrandName] = useState("");

    // --- variations ---
    const [hasVariations, setHasVariations] = useState(false);
    const [variations, setVariations] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productOptionsLoading, setProductOptionsLoading] = useState(false);

    // --- details ---
    const [shortDescription, setShortDescription] = useState("");
    const [shortDescriptionZh, setShortDescriptionZh] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionZh, setDescriptionZh] = useState("");
    const [additionalDetails, setAdditionalDetails] = useState("");

    // --- inventory ---
    const [outOfStock, setOutOfStock] = useState(false);
    const [expiryDate, setExpiryDate] = useState("");

    // --- ratings ---
    const [showRatings, setShowRatings] = useState(true);

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar("Product created", { variant: "success" });
            navigate('/admin/products');
            dispatch({ type: NEW_PRODUCT_RESET });
        }
    }, [dispatch, error, success, enqueueSnackbar, navigate]);

    // Loads every product (including drafts) once, so variations can be
    // linked to an existing product via search. Only fetched when the
    // admin actually turns on variations, to avoid the cost otherwise.
    useEffect(() => {
        if (!hasVariations || productOptions.length > 0) return;
        const fetchProducts = async () => {
            setProductOptionsLoading(true);
            try {
                const { data } = await axios.get('/api/v1/admin/products');
                setProductOptions(data.products);
            } catch (err) {
                console.error('Failed to load products for linking', err);
            } finally {
                setProductOptionsLoading(false);
            }
        };
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasVariations]);

    const handleMainImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const dataUrl = await readAsDataURL(file);
        setMainImage(dataUrl);
        setMainImagePreview(dataUrl);
    };

    const handleAdditionalImagesChange = async (e) => {
        const files = Array.from(e.target.files).slice(0, 9 - additionalImages.length);
        const dataUrls = await Promise.all(files.map(readAsDataURL));
        setAdditionalImages((prev) => [...prev, ...dataUrls]);
    };

    const removeAdditionalImage = (index) => {
        setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    };

    // --- variation helpers ---
    const updateVariation = (index, patch) => {
        setVariations((prev) => prev.map((v, i) => i === index ? { ...v, ...patch } : v));
    };
    const addVariation = () => setVariations((prev) => [...prev, emptyVariation()]);
    const removeVariation = (index) => setVariations((prev) => prev.filter((_, i) => i !== index));

    const uploadImageToCloudinary = async (dataUrl) => {
        const { data } = await axios.post('/api/v1/admin/upload-image', { image: dataUrl });
        return { url: data.url, public_id: data.public_id };
    };

    const handleVariationImagesChange = async (index, e) => {
        const files = Array.from(e.target.files);
        try {
            const uploaded = await Promise.all(files.map(async (file) => {
                const dataUrl = await readAsDataURL(file);
                return uploadImageToCloudinary(dataUrl);
            }));
            setVariations((prev) => prev.map((v, i) => i === index ? { ...v, images: [...v.images, ...uploaded] } : v));
        } catch (err) {
            enqueueSnackbar("Failed to upload variation image", { variant: "error" });
        }
    };

    const removeVariationImage = (varIndex, imgIndex) => {
        setVariations((prev) => prev.map((v, i) => i === varIndex ? { ...v, images: v.images.filter((_, j) => j !== imgIndex) } : v));
    };



    const buildFormData = (status) => {
        const formData = new FormData();
        formData.set("name", name);
        formData.set("nameZh", nameZh);
        formData.set("shortDescription", shortDescription);
        formData.set("shortDescriptionZh", shortDescriptionZh);
        formData.set("description", description);
        formData.set("descriptionZh", descriptionZh);
        formData.set("additionalDetails", additionalDetails);
        formData.set("price", salePrice);
        formData.set("cuttedPrice", price);
        formData.set("brandname", brandName);
        formData.set("outOfStock", outOfStock);
        if (expiryDate) formData.set("expiryDate", expiryDate);
        formData.set("showRatings", showRatings);
        formData.set("hasVariations", hasVariations);
        formData.set("status", status);

        categories.forEach((id) => formData.append("categories", id));

        const allImages = [mainImage, ...additionalImages].filter(Boolean);
        allImages.forEach((img) => formData.append("images", img));

        if (hasVariations) {
            const cleanVariations = variations.map((v) => v.mode === "linked"
                ? { mode: "linked", linkedProduct: v.linkedProduct?._id }
                : {
                    mode: "inline",
                    name: v.name,
                    price: v.price,
                    cuttedPrice: v.cuttedPrice,
                    categories: v.categories,
                    images: v.images,
                    brand: v.brand,
                });
            formData.set("variations", JSON.stringify(cleanVariations));
        }

        return formData;
    };

    const submitHandler = async (status) => {
        if (!name.trim()) return enqueueSnackbar("Please enter a product name", { variant: "warning" });
        if (!mainImage) return enqueueSnackbar("Please choose a main image", { variant: "warning" });
        if (!salePrice) return enqueueSnackbar("Please enter a sale price", { variant: "warning" });
        if (categories.length === 0) return enqueueSnackbar("Please select at least one category", { variant: "warning" });

        setSaving(true);
        const formData = buildFormData(status);
        await dispatch(createProduct(formData));
        setSaving(false);
    };

    const ActionButtons = () => (
        <div className="flex justify-end gap-3">
            <button type="button" onClick={() => submitHandler("draft")} className="border border-gray-400 text-gray-700 uppercase px-6 py-3 font-medium rounded hover:bg-gray-50">
                Save as Draft
            </button>
            <button type="button" onClick={() => submitHandler("published")} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">
                Publish
            </button>
        </div>
    );

    return (
        <>
            <MetaData title="Admin: New Product | 1313 Mart" />
            {(loading || saving) && <BackdropLoader />}

            <div className="flex flex-col gap-6">

                <ActionButtons />

                {/* Basic Info */}
                <div className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="font-medium text-lg">Basic Info</h2>

                    <TextField label="Name (English)" variant="outlined" size="small" required fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                    <TextField label="Name (中文 — optional)" variant="outlined" size="small" fullWidth value={nameZh} onChange={(e) => setNameZh(e.target.value)} helperText="Shown on the website when a visitor switches to Chinese. Leave blank to keep showing the English name." />

                    <div className="flex flex-col sm:flex-row gap-6">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Main Image</p>
                            <div className="flex items-center gap-3">
                                <div className="w-28 h-28 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                                    {!mainImagePreview ? <ImageIcon className="text-gray-400" /> :
                                        <img draggable="false" src={mainImagePreview} alt="Main" className="w-full h-full object-cover" />
                                    }
                                </div>
                                <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                                    <input type="file" accept="image/*" onChange={handleMainImageChange} className="hidden" />
                                    Choose
                                </label>
                            </div>
                        </div>

                        <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-1">Additional Images (up to 9)</p>
                            <div className="flex flex-wrap gap-2">
                                {additionalImages.map((img, i) => (
                                    <div key={i} className="relative w-20 h-20 border rounded-lg overflow-hidden">
                                        <img draggable="false" src={img} alt={`Additional ${i}`} className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => removeAdditionalImage(i)} className="absolute top-0 right-0 bg-black/60 text-white rounded-bl px-1 text-xs">✕</button>
                                    </div>
                                ))}
                                {additionalImages.length < 9 && (
                                    <label className="w-20 h-20 flex items-center justify-center border-dashed border-2 rounded-lg cursor-pointer text-gray-400 hover:text-gray-600">
                                        <input type="file" accept="image/*" multiple onChange={handleAdditionalImagesChange} className="hidden" />
                                        <AddIcon />
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <TextField label="Price" type="number" variant="outlined" size="small" fullWidth value={price} onChange={(e) => setPrice(e.target.value)} helperText="Regular price (shown struck-through if higher than sale price)" />
                        <TextField label="Sale Price" type="number" variant="outlined" size="small" required fullWidth value={salePrice} onChange={(e) => setSalePrice(e.target.value)} helperText="The actual price customers pay" />
                    </div>

                    <CategoryMultiSelect value={categories} onChange={setCategories} label="Categories (select one or more)" />

                    <BrandSelect value={brandName} onChange={setBrandName} />
                </div>

                {/* Variations */}
                <div className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6">
                    <FormControlLabel
                        control={<Switch checked={hasVariations} onChange={(e) => setHasVariations(e.target.checked)} />}
                        label={<span className="font-medium text-lg">Variations</span>}
                    />

                    {hasVariations && (
                        <div className="flex flex-col gap-4">
                            {variations.map((v, i) => (
                                <div key={i} className="border rounded-lg p-4 flex flex-col gap-3 bg-gray-50">
                                    <div className="flex justify-between items-center">
                                        <TextField
                                            label="Variation Type"
                                            select
                                            variant="outlined"
                                            size="small"
                                            value={v.mode}
                                            onChange={(e) => updateVariation(i, { mode: e.target.value })}
                                            sx={{ minWidth: 220 }}
                                        >
                                            <MenuItem value="inline">Define a new variation</MenuItem>
                                            <MenuItem value="linked">Link an existing product</MenuItem>
                                        </TextField>
                                        <button type="button" onClick={() => removeVariation(i)} className="text-red-500 hover:text-red-700 p-2"><DeleteIcon /></button>
                                    </div>

                                    {v.mode === "linked" ? (
                                        <Autocomplete
                                            size="small"
                                            options={productOptions}
                                            loading={productOptionsLoading}
                                            getOptionLabel={(opt) => opt.name || ""}
                                            isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                            value={v.linkedProduct}
                                            onChange={(e, newValue) => updateVariation(i, { linkedProduct: newValue })}
                                            renderInput={(params) => <TextField {...params} label="Search existing products" placeholder="Type a product name..." />}
                                        />
                                    ) : (
                                        <>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <TextField label="Name" variant="outlined" size="small" fullWidth value={v.name} onChange={(e) => updateVariation(i, { name: e.target.value })} />
                                                <TextField label="Price" type="number" variant="outlined" size="small" fullWidth value={v.price} onChange={(e) => updateVariation(i, { price: e.target.value })} />
                                                <TextField label="Sale Price" type="number" variant="outlined" size="small" fullWidth value={v.cuttedPrice} onChange={(e) => updateVariation(i, { cuttedPrice: e.target.value })} />
                                            </div>

                                            <CategoryMultiSelect value={v.categories} onChange={(val) => updateVariation(i, { categories: val })} label="Categories" />

                                            <BrandSelect
                                                value={v.brand.name}
                                                onChange={(newName) => updateVariation(i, { brand: { name: newName } })}
                                                label="Brand (optional)"
                                            />

                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Images</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {v.images.map((img, j) => (
                                                        <div key={j} className="relative w-16 h-16 border rounded-lg overflow-hidden">
                                                            <img draggable="false" src={img.url} alt="" className="w-full h-full object-cover" />
                                                            <button type="button" onClick={() => removeVariationImage(i, j)} className="absolute top-0 right-0 bg-black/60 text-white rounded-bl px-1 text-xs">✕</button>
                                                        </div>
                                                    ))}
                                                    <label className="w-16 h-16 flex items-center justify-center border-dashed border-2 rounded-lg cursor-pointer text-gray-400 hover:text-gray-600">
                                                        <input type="file" accept="image/*" multiple onChange={(e) => handleVariationImagesChange(i, e)} className="hidden" />
                                                        <AddIcon />
                                                    </label>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={addVariation} className="flex items-center gap-1 text-sm text-primary-blue hover:underline w-fit">
                                <AddIcon sx={{ fontSize: "18px" }} /> Add Variation
                            </button>
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="font-medium text-lg">Details</h2>
                    <TextField label="Short Description (optional)" variant="outlined" size="small" fullWidth multiline minRows={2} value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} />
                    <TextField label="Short Description (中文 — optional)" variant="outlined" size="small" fullWidth multiline minRows={2} value={shortDescriptionZh} onChange={(e) => setShortDescriptionZh(e.target.value)} />
                    <TextField label="Description (optional)" variant="outlined" size="small" fullWidth multiline minRows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                    <TextField label="Description (中文 — optional)" variant="outlined" size="small" fullWidth multiline minRows={4} value={descriptionZh} onChange={(e) => setDescriptionZh(e.target.value)} />
                    <TextField label="Additional Details (optional)" variant="outlined" size="small" fullWidth multiline minRows={4} value={additionalDetails} onChange={(e) => setAdditionalDetails(e.target.value)} />
                </div>

                {/* Inventory */}
                <div className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="font-medium text-lg">Inventory</h2>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <FormControlLabel
                            control={<Checkbox checked={outOfStock} onChange={(e) => setOutOfStock(e.target.checked)} />}
                            label="Mark as Out of Stock"
                        />
                        <TextField label="Expiry Date (optional)" type="date" variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
                    </div>
                </div>

                {/* Ratings */}
                <div className="flex flex-col gap-2 bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="font-medium text-lg">Ratings</h2>
                    <FormControlLabel
                        control={<Checkbox checked={showRatings} onChange={(e) => setShowRatings(e.target.checked)} />}
                        label="Show star rating & review count on the product page"
                    />
                </div>

                <ActionButtons />

            </div>
        </>
    );
};

export default NewProduct;